import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Trophy, 
  Users, 
  Calendar, 
  Settings, 
  Play, 
  QrCode,
  FileDown,
  BarChart3,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Circle,
  Zap,
  Shield,
  Monitor,
  Smartphone,
  HelpCircle,
  BookOpen,
  Layers
} from 'lucide-react';

const HelpGuide = () => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState('workflow');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'workflow',
      title: 'Tournament Organizer Workflow',
      icon: Trophy,
      color: 'text-primary',
      steps: [
        { title: 'Step 1: Create Tournament', description: 'Create the overall event: name, venue, start/end dates (start date cannot be in the past), and settings.', tip: 'Tournament is the container; it does not create matches by itself.' },
        { title: 'Step 2: Create Divisions', description: 'Create eligibility groups (e.g., Open, U1500, Women, U18).', tip: 'Divisions group players fairly; they do not create draws or matches.' },
        { title: 'Step 3: Add or Import Players', description: 'Add players to the tournament, then assign them to divisions. Players are per-tournament.', tip: 'Add players before creating competitions so you can add them as participants.' },
        { title: 'Step 4: Create Competitions', description: 'Inside each division, create competitions: choose sport, discipline (Singles/Doubles/Mixed), format (Round Robin, Single Elimination, Groups + Knockout), and scoring rules.', tip: 'Example: Open (division) → Table Tennis → Singles → Round Robin or Groups + Knockout.' },
        { title: 'Step 5: Add Participants', description: 'For each competition, add participants (players or teams) from your tournament player pool.', tip: 'You need at least 2 participants to generate a draw.' },
        { title: 'Step 6: Generate Draw', description: 'Generate the draw (round robin, single elimination, or groups). You can reseed (random, by rating, or manual) before generating.', tip: 'For Round Robin you can later choose "Generate Knockout" for semi-finals + final, or "Set finalists" for a single final.' },
        { title: 'Step 7: Use Control Desk', description: 'Assign the next ready match to a resource (table/court), start it, then enter the score.', tip: 'Scores update standings automatically; winners advance in knockout.' },
        { title: 'Step 8: View Results', description: 'View standings and results; export or share the Live Match Center and Public Board.', tip: 'Standings are per competition; leaderboards can show cross-tournament rankings.' }
      ]
    },
    {
      id: 'division-vs-competition',
      title: 'Division vs Competition',
      icon: Layers,
      color: 'text-amber-500',
      steps: [
        { title: 'Division', description: 'An eligibility bucket (age, rating, gender) used to group players fairly. Divisions do NOT create matches.', tip: 'Examples: Open, U1500, Women, U18.' },
        { title: 'Competition', description: 'A playable event bracket inside a division. It has a sport, discipline (Singles/Doubles/Mixed), format (Round Robin, Single Elimination, Groups to Knockout), and scoring rules. Competitions DO create matches.', tip: 'One division can have multiple competitions (e.g., Open Singles and Open Doubles).' }
      ]
    },
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Zap,
      color: 'text-yellow-500',
      steps: [
        {
          title: 'Create an Account',
          description: 'Register with your email and password, or use Google login for quick access.',
          tip: 'The first registered user becomes the Admin with full access.'
        },
        {
          title: 'Verify Your Email',
          description: 'Check your inbox for a verification email and click the link to activate your account.',
          tip: 'Some features may be restricted until you verify your email.'
        },
        {
          title: 'Access Dashboard',
          description: 'After login, you\'ll see your Dashboard with quick stats and recent tournaments.',
          tip: 'Use the sidebar to navigate between different sections.'
        }
      ]
    },
    {
      id: 'create-tournament',
      title: 'Creating a Tournament',
      icon: Trophy,
      color: 'text-red-500',
      steps: [
        {
          title: 'Click "New Tournament"',
          description: 'From Dashboard or Tournaments page, click "New Tournament" or "Create Tournament".',
          tip: 'Plan your tournament structure before creating it.'
        },
        {
          title: 'Fill Tournament Details',
          description: 'Enter name, venue, start date (must be today or later), optional end date (on or after start), and description.',
          tip: 'Start date cannot be in the past; end date must be on or after start date.'
        },
        {
          title: 'Configure Settings',
          description: 'Set min rest between matches, buffer time, and optional default duration per sport.',
          tip: 'Scoring rules (sets to win, points per set) are set per competition, not per tournament.'
        },
        {
          title: 'Save Tournament',
          description: 'Your tournament is created in "Draft" status. You can now add divisions, players, and competitions.',
          tip: 'Tournament status will auto-update as matches progress.'
        }
      ]
    },
    {
      id: 'manage-players',
      title: 'Managing Players',
      icon: Users,
      color: 'text-blue-500',
      steps: [
        {
          title: 'Open Tournament',
          description: 'Players belong to a tournament. Open the tournament, then go to the Players tab.',
          tip: 'Each tournament has its own player list; add or import players there.'
        },
        {
          title: 'Add Players Manually',
          description: 'Click "Add Player" and enter First Name, Last Name, Email, Phone, Age (optional), and optional Skill Level or Rating.',
          tip: 'Rating helps with seeded draws (e.g. by rating or manual order).'
        },
        {
          title: 'Import from CSV',
          description: 'For bulk import, use the CSV import with columns: firstName, lastName, email, phone, gender, age, skillLevel, sports, rating, team, division, club.',
          tip: 'Download the template to see the correct format.'
        },
        {
          title: 'Add to Competitions',
          description: 'From each competition, add participants (players or teams) from the tournament player pool.',
          tip: 'One player can participate in multiple competitions in the same tournament.'
        }
      ]
    },
    {
      id: 'setup-competition',
      title: 'Setting Up Competitions',
      icon: Calendar,
      color: 'text-green-500',
      steps: [
        {
          title: 'Open Tournament Detail',
          description: 'Click on a tournament to view its details and manage competitions.',
          tip: 'A tournament can have multiple competitions (e.g., Men\'s Singles, Women\'s Doubles).'
        },
        {
          title: 'Create Competition',
          description: 'Click "Add Competition". Choose sport, division (required), discipline, and format. Competitions create draws and matches.',
          tip: 'Competition must belong to a division. Example: Open → Table Tennis → Singles → Groups to Knockout.'
        },
        {
          title: 'Add Participants & Generate Draw',
          description: 'Click "Manage" on a competition to open its page. This is the only place where you add participants (players/teams from that division) and generate the draw. Add at least 2 participants, then use Generate Draw and choose seeding (Random, By Rating, or Manual).',
          tip: 'Participants and drawing are managed only from each competition\'s Manage page—not from the tournament overview.'
        }
      ]
    },
    {
      id: 'round-robin-knockout',
      title: 'Round Robin Then Elimination',
      icon: Trophy,
      color: 'text-blue-500',
      steps: [
        {
          title: 'Choose Round Robin',
          description: 'When creating a competition, select format "Round Robin". Generate the draw to create the full round robin: for N players you get N−1 rounds (each player plays everyone else once). Example: 8 players = 7 rounds, 28 matches; 6 players = 5 rounds, 15 matches. A placeholder Final is also created.',
          tip: 'Round count is correct: 5 rounds for 6 players means each player plays 5 games (everyone else once).'
        },
        {
          title: 'Complete Round-Robin Matches',
          description: 'Run all round-robin matches. Standings are calculated automatically.',
          tip: 'The TBD Final does not need to be played in the round-robin phase.'
        },
        {
          title: 'Choose Next Phase',
          description: 'When round robin is done, you can either "Set finalists from standings" (1st vs 2nd, one final) or "Generate Knockout". Generate Knockout advances the top 50% (e.g. 8 players → top 4 to semi-finals + final; 6 players → top 3 with one bye to final) and removes the placeholder final.',
          tip: 'Top 50% advance to knockout; the bracket size is chosen automatically.'
        }
      ]
    },
    {
      id: 'groups-knockout',
      title: 'Groups + Knockout Format',
      icon: Trophy,
      color: 'text-amber-500',
      steps: [
        {
          title: 'When to Use Groups + Knockout',
          description: 'Ideal for larger tournaments (8+ players) where you want every player to have multiple guaranteed matches before knockout rounds.',
          tip: 'This format combines round-robin group play with elimination brackets.'
        },
        {
          title: 'Create Groups + Knockout Competition',
          description: 'When creating a competition, select "Groups + Knockout" as the format. Set the number of groups and how many players advance from each group.',
          tip: 'Example: 8 players, 2 groups, top 2 advance = 4 players in knockout.'
        },
        {
          title: 'Understanding the Group Stage',
          description: 'Players are distributed into groups using snake draft (balanced seeding). Each player plays everyone in their group once (round-robin).',
          tip: 'Standings show Played (P), Wins (W), Losses (L), and Point Difference (+/-).'
        },
        {
          title: 'Complete Group Matches',
          description: 'Run all group stage matches. The standings table updates automatically as matches are completed.',
          tip: 'Top players in each group (shown with green highlighting) will advance to knockout.'
        },
        {
          title: 'Generate Knockout Bracket',
          description: 'Once ALL group matches are complete, click "Generate Knockout" to create the elimination bracket with qualified players.',
          tip: 'The button only appears when the group stage is 100% complete.'
        },
        {
          title: 'Finish with Knockout',
          description: 'The knockout bracket appears below the groups. Run these matches to determine the champion.',
          tip: 'Knockout matches show as Semi-Final, Final, etc. based on remaining players.'
        }
      ]
    },
    {
      id: 'manage-resources',
      title: 'Managing Resources (Courts/Tables)',
      icon: Settings,
      color: 'text-purple-500',
      steps: [
        {
          title: 'Open Tournament Resources',
          description: 'From the tournament, open the Resources tab to manage courts and tables for that event.',
          tip: 'Resources are per tournament; add the tables/courts available for this event.'
        },
        {
          title: 'Add Resources',
          description: 'Click "Add Resource", choose sport, enter label (e.g., "Table 1", "Court A"), and optional location.',
          tip: 'Add all available tables/courts before the tournament day.'
        },
        {
          title: 'Assign to Matches',
          description: 'In Control Desk, assign matches to specific resources so referees and players know where to go.',
          tip: 'Match status becomes "Scheduled" when assigned to a resource.'
        }
      ]
    },
    {
      id: 'run-matches',
      title: 'Running Matches (Control Desk)',
      icon: Play,
      color: 'text-orange-500',
      steps: [
        {
          title: 'Access Control Desk',
          description: 'From Tournament Detail, click "Control Desk" to manage live matches.',
          tip: 'Control Desk is your command center during the tournament.'
        },
        {
          title: 'View Pending Matches',
          description: 'See all matches waiting to be played, organized by round.',
          tip: 'Matches are auto-generated when you create the draw.'
        },
        {
          title: 'Assign Match to Resource',
          description: 'Drag a match to a table/court or use the assign button.',
          tip: 'This marks the match as "Scheduled".'
        },
        {
          title: 'Start Match',
          description: 'Click on a scheduled match and hit "Start Match" to begin scoring.',
          tip: 'The match status changes to "Live".'
        },
        {
          title: 'Enter Scores',
          description: 'Use + and - buttons to update scores for each player during each set.',
          tip: 'Click "End Set" when a set is complete to move to the next.'
        },
        {
          title: 'Match Auto-Completes',
          description: 'When a player wins the required sets, the match automatically ends.',
          tip: 'Winner advances to the next round automatically.'
        }
      ]
    },
    {
      id: 'digital-scoring',
      title: 'Digital Scoring with QR Code',
      icon: QrCode,
      color: 'text-cyan-500',
      steps: [
        {
          title: 'Open Match Scoreboard',
          description: 'Click on a live match to open the scoreboard interface.',
          tip: 'You can score directly or delegate to a referee.'
        },
        {
          title: 'Generate Referee QR Code',
          description: 'Click "Referee QR Code" button to generate access for a scorer.',
          tip: 'This creates a one-time code valid for this match only.'
        },
        {
          title: 'Share with Referee',
          description: 'Show the QR code to the referee or share the 6-digit OTP code.',
          tip: 'Referee can scan QR or enter code at the scoring URL.'
        },
        {
          title: 'Referee Enters Scores',
          description: 'The referee uses their phone to update scores in real-time.',
          tip: 'No login required - the code provides temporary access.'
        },
        {
          title: 'Scores Update Live',
          description: 'All score updates appear in real-time on the Live Match Center.',
          tip: 'Spectators can follow along from anywhere!'
        }
      ]
    },
    {
      id: 'score-sheets',
      title: 'PDF Score Sheets',
      icon: FileDown,
      color: 'text-pink-500',
      steps: [
        {
          title: 'Access Competition Detail',
          description: 'Navigate to the competition you want to print score sheets for.',
          tip: 'Score sheets work best when the draw is already generated.'
        },
        {
          title: 'Click "Score Sheet PDF"',
          description: 'Click the button to download a PDF with all match score sheets.',
          tip: 'Each match gets its own page with player names and scoring grid.'
        },
        {
          title: 'Print and Distribute',
          description: 'Print the PDF and give sheets to referees for manual scoring backup.',
          tip: 'Great for venues with limited WiFi or as backup.'
        }
      ]
    },
    {
      id: 'leaderboards',
      title: 'Leaderboards & Live Center',
      icon: BarChart3,
      color: 'text-emerald-500',
      steps: [
        {
          title: 'Access Live Match Center',
          description: 'Click "Live" in the header to see all currently active matches.',
          tip: 'This page auto-updates every few seconds.'
        },
        {
          title: 'View Leaderboards',
          description: 'Click "Leaderboards" dropdown and select a sport to see rankings.',
          tip: 'Rankings are based on wins, losses, and win rate.'
        },
        {
          title: 'Tournament Standings',
          description: 'From Tournament Detail, click "Standings" to see competition-specific rankings.',
          tip: 'Standings show sets won/lost and points scored.'
        },
        {
          title: 'Share with Spectators',
          description: 'Share the Live Match Center URL with spectators for real-time updates.',
          tip: 'No login required to view public leaderboards!'
        }
      ]
    }
  ];

  const quickTips = [
    { icon: Shield, text: 'Add Moderators to help manage your tournament' },
    { icon: Monitor, text: 'Use a tablet at each table for easy scoring' },
    { icon: Smartphone, text: 'QR codes work great on mobile phones' },
    { icon: CheckCircle2, text: 'Verify your email to unlock all features' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-b">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase">
                How to Use RallyDesk
              </h1>
              <p className="text-muted-foreground mt-1">
                Complete guide to running your tournament like a pro
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Quick Tips */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-primary" />
              Quick Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {quickTips.map((tip, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-background rounded-lg">
                  <tip.icon className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm">{tip.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Flow Overview */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {sections.slice(0, 6).map((section, idx) => (
            <React.Fragment key={section.id}>
              <Badge 
                variant="outline" 
                className={`cursor-pointer whitespace-nowrap ${expandedSection === section.id ? 'bg-primary/10 border-primary' : ''}`}
                onClick={() => setExpandedSection(section.id)}
              >
                <span className="mr-1">{idx + 1}</span>
                {section.title.split(' ')[0]}
              </Badge>
              {idx < 5 && <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section, sectionIdx) => (
            <Card 
              key={section.id} 
              className={`transition-all duration-200 ${expandedSection === section.id ? 'ring-2 ring-primary/30' : ''}`}
            >
              <CardHeader 
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleSection(section.id)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-muted flex items-center justify-center`}>
                      <section.icon className={`w-5 h-5 ${section.color}`} />
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Step {sectionIdx + 1}</span>
                      <h3 className="font-heading text-xl">{section.title}</h3>
                    </div>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-muted-foreground transition-transform ${
                      expandedSection === section.id ? 'rotate-180' : ''
                    }`} 
                  />
                </CardTitle>
              </CardHeader>
              
              {expandedSection === section.id && (
                <CardContent className="pt-0">
                  <div className="space-y-4 pl-4 border-l-2 border-primary/20 ml-5">
                    {section.steps.map((step, stepIdx) => (
                      <div key={stepIdx} className="relative pl-6">
                        <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        </div>
                        <h4 className="font-semibold mb-1">{step.title}</h4>
                        <p className="text-muted-foreground text-sm mb-2">{step.description}</p>
                        {step.tip && (
                          <div className="flex items-start gap-2 text-xs bg-muted/50 p-2 rounded">
                            <HelpCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground"><strong>Tip:</strong> {step.tip}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Footer CTA */}
        <Card className="mt-8 bg-gradient-to-r from-primary/10 to-primary/5">
          <CardContent className="p-6 text-center">
            <Trophy className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h3 className="font-heading text-2xl font-bold mb-2">Ready to Start?</h3>
            <p className="text-muted-foreground mb-4">
              Create your first tournament and see how easy it is!
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate('/tournaments/new')}>
                Create Tournament
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HelpGuide;
